import React, { useEffect, useState } from "react";

import {
  Typography,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  AccordionActions,
  Divider,
  Skeleton,
} from "@mui/material";
import { Edit, ExpandMore } from "@mui/icons-material";
import { makeStyles } from "tss-react/mui";

import { fetchInvestments } from "libLegacy/api/dashboard";
import { InvestmentsResult } from "libLegacy/api/models";

import { InfoCard } from "components/admin/InfoCard";
import { ChartPayback } from "components/charts/ChartPayback";
import { ChartProfitability } from "components/charts/ChartProfitability";
import { ButtonLink } from "components/MUIExtensions/ButtonLink";
import { PageStandard } from "components/admin/PageStandard";
import { BlockStandard } from "components/admin/BlockStandard";
import { useToken } from "contexts/UserContext";
import { useSetError } from "contexts/ErrorContext";

const useStyles = makeStyles()((theme) => ({
  root: {
    backgroundColor: theme.palette.common.white,
    padding: theme.spacing(4, 2),
    "& > :not(:last-child)": {
      marginBottom: theme.spacing(2),
    },
  },
  charts: {
    borderRadius: "18px",
    border: `1px solid ${theme.palette.primary.main}`,
    minHeight: "420px",
    gap: theme.spacing(2),
    padding: theme.spacing(2),
  },
  divider: {
    marginBottom: theme.spacing(2),
  },
}));

const LOCALE = "pt-br";

export const Investments = ({ history }) => {
  const [data, setData] = useState<InvestmentsResult>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { classes } = useStyles();
  const token = useToken();
  const setGlobalError = useSetError();

  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      try {
        const data = await fetchInvestments(token);
        if (data?.unitNeedsConfiguration) {
          history.push("/admin/investments/edit");
        } else {
          setData(data);
        }
      } catch (error) {
        // @ts-expect-error
        setGlobalError(error.response?.data?.error);
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [history, token, setGlobalError]);

  const formatEstimatedPayback = (): string => {
    let formattedData = "";
    if (data) {
      const totalTime = data.estimatedPaybackTime;
      const years = Math.floor(totalTime / 12);
      const months = totalTime % 12;

      const yearsText = years ? `${years} ${years === 1 ? "ano" : "anos"}` : "";
      const joiner = months && years ? " e " : "";
      const monthsText = months
        ? `${months} ${months === 1 ? "mês" : "meses"}`
        : "";

      formattedData = yearsText + joiner + monthsText;
    }
    return formattedData;
  };

  if (isLoading) {
    return (
      <PageStandard
        hideDivider
        title="Investimentos"
        subtitle="Veja o resultado de seus investimentos."
      >
        <Grid
          container
          item
          direction="column"
          alignItems="center"
          sx={{ flex: 1, padding: 2 }}
        >
          <Skeleton
            variant="rectangular"
            width="100%"
            height="30%"
            sx={{ position: "absolute", top: "35%" }}
          />
        </Grid>
      </PageStandard>
    );
  }

  return (
    <PageStandard
      hideDivider
      title="Investimentos"
      subtitle="Veja o resultado de seus investimentos."
    >
      <BlockStandard hideDivider>
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography variant="subtitle1">
              Detalhamento do investimento
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid
              container
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography>Data de Instalação</Typography>
              <Typography variant="subtitle1">
                {data?.installationDate ? (
                  new Date(data.installationDate).toLocaleDateString()
                ) : (
                  <Skeleton />
                )}
              </Typography>
            </Grid>
            <Divider className={classes.divider} />
            <Grid
              container
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography>Total investido</Typography>
              <Typography variant="subtitle1">
                {data?.investedAmountBrl ? (
                  (+data.investedAmountBrl).toLocaleString(LOCALE, {
                    style: "currency",
                    currency: "BRL",
                  })
                ) : (
                  <Skeleton />
                )}
              </Typography>
            </Grid>
            <Divider className={classes.divider} />
            <Grid
              container
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography>Economia Mensal Esperada</Typography>
              <Typography variant="subtitle1">
                {data?.expectedMonthlyEconomyBrl ? (
                  (+data.expectedMonthlyEconomyBrl).toLocaleString(LOCALE, {
                    style: "currency",
                    currency: "BRL",
                  })
                ) : (
                  <Skeleton />
                )}
              </Typography>
            </Grid>
            <Divider />
          </AccordionDetails>
          <AccordionActions>
            <ButtonLink
              disabled={isLoading}
              to={{
                pathname: "/admin/investments/edit",
                state: {
                  expectedEconomy: data?.expectedMonthlyEconomyBrl,
                  investedAmount: data?.investedAmountBrl,
                  instalationDate: data?.installationDate,
                },
              }}
            >
              Editar valores de investimento
              <Edit sx={{ ml: 1 }} />
            </ButtonLink>
          </AccordionActions>
        </Accordion>
      </BlockStandard>

      <BlockStandard hideDivider>
        <Grid
          item
          container
          justifyContent="center"
          wrap="nowrap"
          sx={{ gap: 1 }}
        >
          <InfoCard
            loading={isLoading}
            title="Taxa Interna de Retorno (Estimada)"
            value={
              data?.estimatedReturnRate.toLocaleString(LOCALE, {
                style: "percent",
              }) || ""
            }
          />
          <InfoCard
            loading={isLoading}
            title="Payback (Estimado)"
            value={formatEstimatedPayback()}
          />
        </Grid>
      </BlockStandard>

      <BlockStandard hideDivider>
        <Grid container sx={{ gap: 2 }}>
          <Grid item flexDirection="column" className={classes.charts}>
            <ChartPayback
              investedAmount={data?.investedAmountBrl}
              estimatedPayback={data?.estimatedPaybackEntries}
              realPayback={data?.realPaybackEntries}
              loading={isLoading}
            />
          </Grid>
          <Grid item flexDirection="column" className={classes.charts}>
            <ChartProfitability
              estimatedProfitability={data?.estimatedProfitabilityEntries}
              realProfitability={data?.realProfitabilityEntries}
              loading={isLoading}
            />
          </Grid>
        </Grid>
      </BlockStandard>
    </PageStandard>
  );
};
